import React from 'react';
import { Bell } from 'lucide-react';
import { Button } from '../../ui/Button';

interface BulletinsSettingsProps {
  generalSettings: {
    bulletinsEnabled: boolean;
  };
  isBulletinsSettingsSaving: boolean;
  onBulletinsChange: (enabled: boolean) => void;
  onSave: () => void;
  onReset: () => void;
}

export const BulletinsSettings: React.FC<BulletinsSettingsProps> = ({
  generalSettings,
  isBulletinsSettingsSaving,
  onBulletinsChange,
  onSave,
  onReset
}) => {
  return (
    <div>
      <div className="mb-6">
        <h4 className="text-lg font-medium flex items-center" style={{ color: 'var(--color-text-primary)' }}>
          <Bell className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
          Bulletins System
        </h4>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Configure the bulletins feature for announcements and notifications
        </p>
      </div>
      
      <div className="max-w-2xl space-y-6">
        <div className="flex items-center justify-between p-4 border rounded-lg" style={{ 
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: 'var(--color-border)'
        }}>
          <div>
            <h5 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              Enable Bulletins Feature
            </h5>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Allow administrators to post system-wide announcements and important notices
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
                          <input
                type="checkbox"
                checked={generalSettings.bulletinsEnabled}
                onChange={(e) => onBulletinsChange(e.target.checked)}
                className="sr-only peer"
              />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        {generalSettings.bulletinsEnabled && (
          <div className="space-y-4 p-4 border rounded-lg" style={{ 
            backgroundColor: 'var(--color-bg-tertiary)',
            borderColor: 'var(--color-border)'
          }}>
            <h6 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              Bulletins Configuration
            </h6>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Priority levels (Low, Medium, High, Critical)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Scheduled publishing</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>User targeting and filtering</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Read receipts and analytics</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Rich text content support</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Email and push notifications</span>
              </div>
            </div>
          </div>
        )}
        
        {!generalSettings.bulletinsEnabled && (
          <div className="p-4 border rounded-lg" style={{ 
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-warning)',
            borderStyle: 'dashed'
          }}>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              The bulletins feature is currently disabled. Enable it above to allow administrators to post system-wide announcements and important notices to users.
            </p>
          </div>
        )}
        
        {/* Save and Reset Buttons */}
        <div className="flex justify-end space-x-3 mt-8">
          <Button
            variant="secondary"
            onClick={onReset}
            size="sm"
          >
            Reset to Defaults
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={isBulletinsSettingsSaving}
            size="sm"
          >
            {isBulletinsSettingsSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};
